import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/ui/table/data-table";
import { DataTableRowActions } from "@/components/ui/table/data-table-row-actions.jsx";
import Loader from "@/components/utils/loader";
import { ArrowUpDown, Pencil, Plus, Trash, UserCog } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";
import {
  createRole,
  deleteRole,
  fetchRoleById,
  fetchRoles,
  updateRole,
} from "@/services/roles";
import CreateRoleForm from "./create-role-form.jsx";
import UpdateRoleForm from "./update-role-form.jsx";

const RolesDataTable = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [roleToUpdate, setRoleToUpdate] = useState(null);

  const loadRoles = async () => {
    const data = await fetchRoles();
    setRoles(data);
  };

  useEffect(() => {
    const run = async () => {
      try {
        await loadRoles();
      } catch (error) {
        console.error("Load roles error", error);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const handleCreateRole = async (payload) => {
    try {
      await createRole(payload);
      toast.success(t("alertDialog.toast.roleCreatedSuccess"));
      await loadRoles();
    } catch (error) {
      console.error("Error creating role", error);
      toast.error(t("alertDialog.toast.roleCreatedError"));
      throw error;
    }
  };

  const handleDeleteRole = async (id) => {
    try {
      await deleteRole(id);
      toast.success(t("alertDialog.toast.roleDeletedSuccess"));
      await loadRoles();
    } catch (error) {
      console.error("Error deleting role", error);
      toast.error(t("alertDialog.toast.roleDeletedError"));
    }
  };

  const handleOpenUpdate = async (id) => {
    try {
      const data = await fetchRoleById(id);
      setRoleToUpdate(data);
      setUpdateModalOpen(true);
    } catch (error) {
      console.error("Error loading role", error);
      toast.error(t("alertDialog.toast.roleUpdatedError"));
    }
  };

  const handleUpdateRole = async (id, payload) => {
    try {
      await updateRole(id, payload);
      toast.success(t("alertDialog.toast.roleUpdatedSuccess"));
      setUpdateModalOpen(false);
      setRoleToUpdate(null);
      await loadRoles();
    } catch (error) {
      console.error("Error updating role", error);
      toast.error(t("alertDialog.toast.roleUpdatedError"));
      throw error;
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: ({ column }) => (
          <Button
            className="uppercase text-xs"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("forms.roles.name")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="ml-4 flex items-center gap-2">
            <UserCog className="h-4 w-4 text-slate-500" />
            <span>{row.getValue("name")}</span>
          </div>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => (
          <DataTableRowActions
            row={row}
            actions={[
              {
                label: t("buttonTexts.delete"),
                onClick: (row) => handleDeleteRole(row.original.id),
                icon: Trash,
              },
              {
                label: t("buttonTexts.edit"),
                onClick: (row) => handleOpenUpdate(row.original.id),
                icon: Pencil,
              },
            ]}
          />
        ),
      },
    ],
    [t],
  );

  return (
    <section className="w-full">
      <Toaster />

      <section className="w-full flex mb-3">
        <Dialog
          open={createModalOpen}
          onOpenChange={(isOpen) => setCreateModalOpen(isOpen)}
        >
          <DialogTrigger asChild={true}>
            <Button className="mt-4 bg-primary hover:bg-primary-600">
              {t("buttonTexts.addNew")} <Plus className="ml-2" />
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("MainLayout.roles.createRole")}</DialogTitle>
            </DialogHeader>
            <CreateRoleForm
              onCreate={handleCreateRole}
              onClose={() => setCreateModalOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </section>

      <section className="w-full flex">
        <Dialog open={updateModalOpen} onOpenChange={(isOpen) => setUpdateModalOpen(isOpen)}>
          <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("MainLayout.roles.updateRole")}</DialogTitle>
            </DialogHeader>
            <UpdateRoleForm
              role={roleToUpdate}
              onUpdate={handleUpdateRole}
              onClose={() => {
                setUpdateModalOpen(false);
                setRoleToUpdate(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </section>

      {loading ? (
        <div className="flex justify-center">
          <Loader style={"loader"} />
        </div>
      ) : (
        <DataTable
          data={roles}
          columns={columns}
          mainFilterColumn="name"
          textPlaceholder={t("filterTextPlaceholders.roles")}
        />
      )}
    </section>
  );
};

export default RolesDataTable;
